import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { featuredVideos, allVideos } from '@/mocks/videos';
import { useState, useEffect } from 'react';

function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroVideos = featuredVideos.slice(0, 4);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroVideos.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroVideos.length]);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroVideos.map((video, idx) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={video.coverImage}
            alt={video.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50">
                {heroVideos[currentSlide].categoryName}
              </span>
              <span className="flex items-center gap-1 text-sm text-background-100">
                <i className="ri-star-fill text-accent-500"></i>
                {heroVideos[currentSlide].rating}
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4 leading-tight">
              {heroVideos[currentSlide].title}
            </h1>
            <p className="text-sm md:text-base text-background-200 mb-6 line-clamp-2 leading-relaxed">
              {heroVideos[currentSlide].description}
            </p>
            <div className="flex items-center gap-4">
              <Link
                to={`/article/${heroVideos[currentSlide].slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${heroVideos[currentSlide].category}`}
                className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-background-100 rounded-lg font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                更多{heroVideos[currentSlide].categoryName}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {heroVideos.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              idx === currentSlide ? 'bg-primary-500 w-7' : 'bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`切换到第${idx + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">视频分类</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {categories.map((cat) => (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors duration-200"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
              <i className={`${cat.icon} text-xl md:text-2xl`}></i>
            </div>
            <span className="text-sm md:text-base font-heading font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
              {cat.name}
            </span>
            <span className="text-[11px] md:text-xs text-foreground-400 text-center leading-relaxed line-clamp-2">
              {cat.description}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeaturedSection() {
  return (
    <section className="bg-background-100 py-10 md:py-14">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">🔥 热门推荐</h2>
            <p className="text-xs md:text-sm text-foreground-500 mt-1">大家都在看的热门内容</p>
          </div>
        </div>
        <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {featuredVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latestVideos = allVideos
    .filter((v) => v.isNew)
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
    .slice(0, 8);

  return (
    <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">🆕 最新上线</h2>
          <p className="text-xs md:text-sm text-foreground-500 mt-1">每日更新，精彩不断</p>
        </div>
      </div>
      <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {latestVideos.map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
    </section>
  );
}

function AllContentSection() {
  const hotVideos = allVideos.filter((v) => v.isHot);

  return (
    <section className="bg-background-100 py-10 md:py-14">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">🎬 全部内容</h2>
            <p className="text-xs md:text-sm text-foreground-500 mt-1">海量精彩视频内容等你探索</p>
          </div>
        </div>
        <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {allVideos.slice(0, 12).map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/category/dianying"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            查看更多内容
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    {
      q: '黑桃视频提供什么类型的视频内容？',
      a: '黑桃视频提供电影、电视剧、综艺节目、动漫番剧、纪录片和短剧等多种类型的高清视频内容推荐和在线观看服务，涵盖国产、日韩、欧美等多个地区的热门作品。',
    },
    {
      q: '黑桃视频的播放是否流畅？需要缓冲吗？',
      a: '黑桃视频采用先进的视频加速技术，支持高清画质播放。在国内主流网络环境下，绝大多数视频可以实现流畅播放，无需长时间缓冲等待。',
    },
    {
      q: '黑桃视频的内容多久更新一次？',
      a: '黑桃视频每天都会更新内容，热门电影、电视剧和综艺节目会在第一时间上线，让用户不错过任何精彩内容。',
    },
    {
      q: '黑桃视频需要付费吗？',
      a: '黑桃视频提供完全免费的视频观看服务，用户无需注册或付费即可畅享全部内容。我们致力于为用户打造最好的免费视频平台。',
    },
    {
      q: '如何找到我想看的视频？',
      a: '您可以通过首页的热门推荐、分类浏览或搜索功能快速找到感兴趣的视频内容。黑桃视频的分类体系清晰完整，涵盖电影、电视剧、综艺、动漫、纪录片等多个类别。',
    },
  ];

  return (
    <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
          常见问题
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <details key={idx} className="group border border-background-200/70 rounded-lg overflow-hidden">
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.q}</span>
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
              </summary>
              <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黑桃视频',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '黑桃视频提供西瓜视频免费、西瓜视频高清全集在线观看，不缓冲流畅播放，最新电视剧电影大全每日更新。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: '{search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '黑桃视频提供什么类型的视频内容？', acceptedAnswer: { '@type': 'Answer', text: '黑桃视频提供电影、电视剧、综艺节目、动漫番剧、纪录片和短剧等多种类型的高清视频内容推荐和在线观看服务。' } },
      { '@type': 'Question', name: '黑桃视频的播放是否流畅？需要缓冲吗？', acceptedAnswer: { '@type': 'Answer', text: '黑桃视频采用先进的视频加速技术，支持高清画质播放，无需长时间缓冲等待。' } },
      { '@type': 'Question', name: '黑桃视频的内容多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '黑桃视频每天都会更新内容，热门内容会在第一时间上线。' } },
      { '@type': 'Question', name: '黑桃视频需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '黑桃视频提供完全免费的视频观看服务，用户无需注册或付费即可畅享全部内容。' } },
      { '@type': 'Question', name: '如何找到我想看的视频？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过首页的热门推荐、分类浏览找到感兴趣的视频内容。' } },
    ],
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑桃视频',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '黑桃视频 - 免费在线观看高清电影、电视剧、综艺、动漫、纪录片',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <Navbar />
      <main>
        <HeroSection />
        <CategorySection />
        <FeaturedSection />
        <LatestSection />
        <AllContentSection />
        <FaqSection />
      </main>
      <Footer />
    </>
  );
}